import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useGlobalContext } from '@honeycomb/data';
import { MapInfoWindow } from './MapInfoWindow';
import { MapMarkerCollection } from './MapMarkerCollection';
export function InteractiveMap({ children, initialCenter, initialZoom = 15, zoomControl = false, mapTypeControl = false, scaleControl = false, streetViewControl = false, rotateControl = false, fullscreenControl = false, height = '100%', width = '100%', style, offscreen, }) {
    const { configSettings: { GoogleMapsAPIKey }, } = useGlobalContext();
    // Refs
    const mapContainerRef = useRef(null);
    const markerBoundsRef = useRef(null);
    const markerCountRef = useRef(0);
    // State
    const [apiLoaded, setApiLoaded] = useState(false);
    const [refreshMap, setRefreshMap] = useState(true);
    const [map, setMap] = useState(null);
    const [selectedMarker, setSelectedMarker] = useState(null);
    const getGoogleMapsApi = React.useCallback(() => {
        return new Promise((resolve) => {
            if (!window.google) {
                // Load the API
                const onLoad = () => resolve(window.google);
                const script = document.createElement('script');
                script.src = `https://maps.googleapis.com/maps/api/js?key=${GoogleMapsAPIKey}&libraries=marker`;
                script.async = true;
                script.addEventListener(`load`, onLoad);
                if (document.body) {
                    document.body.appendChild(script);
                }
            }
            else {
                // API is already loaded
                resolve(window.google);
            }
        });
    }, [GoogleMapsAPIKey]);
    const getMapControls = useCallback(() => ({
        zoomControl,
        mapTypeControl,
        scaleControl,
        streetViewControl,
        rotateControl,
        fullscreenControl,
    }), [fullscreenControl, mapTypeControl, rotateControl, scaleControl, streetViewControl, zoomControl]);
    useEffect(() => {
        getGoogleMapsApi()
            .then(() => {
            setApiLoaded(true);
        })
            // eslint-disable-next-line no-console
            .catch(console.error);
    }, [getGoogleMapsApi]);
    useEffect(() => {
        if (!apiLoaded || !window.google || !refreshMap) {
            return;
        }
        // Create the map
        const mapOptions = Object.assign({ center: initialCenter, zoom: initialZoom, mapId: 'HONEYCOMB_MAP' }, getMapControls());
        const gmap = new window.google.maps.Map(mapContainerRef.current, mapOptions);
        setRefreshMap(false);
        const { current: markerBounds } = markerBoundsRef;
        if (markerBounds !== null && gmap) {
            // only use fitBounds when there's more than one marker or zoom level will be too high
            if (markerCountRef.current === 1) {
                gmap.setCenter(markerBounds.getCenter());
            }
            else {
                gmap.fitBounds(markerBounds);
            }
        }
        setMap(gmap);
    }, [apiLoaded, getMapControls, initialCenter, initialZoom, refreshMap]);
    const handleBoundsKnown = (bounds) => {
        markerBoundsRef.current = bounds;
        if (map !== null) {
            map.panToBounds(bounds);
        }
    };
    let markers = null;
    let infoWindow = null;
    React.Children.forEach(children, (element) => {
        if (element == null) {
            return;
        }
        switch (element.type) {
            case MapMarkerCollection:
                markers = React.cloneElement(element, {
                    map: !offscreen ? map : null,
                    onBoundsKnown: (bounds) => handleBoundsKnown(bounds),
                    onMarkerSelected: (marker) => setSelectedMarker(marker),
                });
                markerCountRef.current = React.Children.count(element.props.children);
                break;
            case MapInfoWindow:
                infoWindow = React.cloneElement(element, {
                    map,
                    marker: selectedMarker,
                });
                break;
            default:
                break;
        }
    });
    const mapStyle = Object.assign({ width,
        height }, style);
    return apiLoaded ? (React.createElement(React.Fragment, null,
        React.createElement("div", { ref: mapContainerRef, style: mapStyle },
            infoWindow != null && infoWindow,
            markers != null && markers),
        React.createElement("style", { 
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML: {
                __html: `
                    .gm-style-iw.gm-style-iw-c {
                        padding: 0;
                    }
                    .gm-style-iw-d {
                        overflow: hidden!important;
                    }
                    body .gm-style .gm-style-iw-t::after{
                        width: 16px;
                        height: 16px;
                        top: -1px;
                    }
                `,
            } }))) : null;
}
